import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Container, Row, Col, Form, Button, ListGroup, Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { sendMessage, getMessages } from '../slices/supportMessageSlice'; // updated import
import ReCAPTCHA from "react-google-recaptcha";
import Loader from '../components/Loader';
import axios from 'axios'; // added

// sanitation constants & helpers
const MAX_MESSAGE_LENGTH = 2500;
const STRIP_CTRL_REGEX = /\p{Cc}/gu; // control chars via Unicode property escape
const HTML_TAG_REGEX = /<[^>]+>/; // any HTML-like tag
const SCRIPT_TAG_REGEX = /<\s*script\b[\s\S]*?<\s*\/\s*script\s*>/i;

function stripControlChars(str) {
  return str.replace(STRIP_CTRL_REGEX, ch => (ch === '\t' ? ch : ''));
}
function removeNewlines(str) {
  return str.replace(/\r\n?|\n/g, ' ');
}
function collapseWhitespace(str) {
  return str.replace(/\s{2,}/g, ' ');
}
function sanitizeOutgoingMessage(raw) {
  let v = (raw ?? '').toString();
  v = v.normalize('NFC');
  v = stripControlChars(v);
  v = removeNewlines(v); // strip all newlines
  v = collapseWhitespace(v);
  v = v.trim();
  if (v.length > MAX_MESSAGE_LENGTH) v = v.slice(0, MAX_MESSAGE_LENGTH);
  return v;
}
function containsProhibitedMarkup(str) {
  return SCRIPT_TAG_REGEX.test(str) || HTML_TAG_REGEX.test(str);
}
// date formatting helper (MM/DD/YYYY H:MM-AM/PM)
function formatTimestamp(ts) {
  const d = new Date(ts);
  if (isNaN(d)) return ts || '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  let h = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${mm}/${dd}/${yyyy} ${h}:${minutes} ${ampm}`;
}

const SupportMessageScreen = ({ history }) => {
  const [content, setContent] = useState('');
  const [grecaptcha, setGrecaptcha] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [recipient, setRecipient] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [messageError, setMessageError] = useState('');
  const [charCount, setCharCount] = useState(0);
  const [recipientQuery, setRecipientQuery] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [validationWarning, setValidationWarning] = useState('');
  const recipientInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const blurTimeoutRef = useRef(null);
  const messagesListRef = useRef(null);
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.user);
  const { messages, error, loading } = useSelector((state) => state.supportMessage);
  // track whether the selected recipient is an external contact sender
  const [recipientIsContact, setRecipientIsContact] = useState(false);

  const isAdmin = !!userInfo?.isAdmin;
  // derive external contacts from contact form messages (visible to all admins)
  const externalContacts = useMemo(() => {
    if (!isAdmin || !messages) return [];
    const map = new Map();
    for (const m of messages) {
      if (m?.isContact && m?.sender) {
        const uname = String(m.sender);
        if (!map.has(uname)) {
          map.set(uname, {
            _id: `contact:${encodeURIComponent(uname)}`,
            username: uname,
            email: m.senderEmail || '',
            isContact: true,
          });
        }
      }
    }
    return Array.from(map.values());
  }, [isAdmin, messages]);

  const combinedUsers = isAdmin ? [...externalContacts, ...allUsers] : [];
  const filteredUsers = isAdmin ? combinedUsers.filter(u => {
    const q = recipientQuery.trim().toLowerCase();
    if (!q) return true;
    return (u.username && u.username.toLowerCase().includes(q)) || (u.email && u.email.toLowerCase().includes(q));
  }) : [];

  const conversationMessages = useMemo(() => {
    if (!messages) return [];
    if (!isAdmin) return messages;
    if (!recipient) return [];
    const me = userInfo?.username;
    if (!me) return [];
    // For contact-form senders, show all their contact submissions (regardless of which admin they were addressed to)
    // and also include any replies between me and that sender.
    if (recipientIsContact) {
      return messages.filter(m => (
        (m.sender === recipient && (m.isContact === true || m.recipient === me)) ||
        (m.sender === me && m.recipient === recipient)
      )).sort((a,b)=> new Date(a.timestamp) - new Date(b.timestamp));
    }
    // default user-to-user convo
    return messages.filter(m => (
      (m.sender === me && m.recipient === recipient) || (m.sender === recipient && m.recipient === me)
    )).sort((a,b)=> new Date(a.timestamp) - new Date(b.timestamp));
  }, [messages, isAdmin, recipient, recipientIsContact, userInfo]);

  // scroll to bottom when conversation updates
  useEffect(() => {
    if (messagesListRef.current) {
      messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight;
    }
  }, [conversationMessages]);

  // auth / initialization effect
  useEffect(() => {
    if (!userInfo) {
      history.push('/login');
    } else {
      if (isAdmin) {
        setRecipient('');
      } else {
        setRecipient('Adminguy007');
      }
      dispatch(getMessages(userInfo.username));
    }
  }, [dispatch, history, userInfo, isAdmin]);

  // fetch users for admin
  useEffect(() => {
    if (isAdmin && userInfo?.token) {
      const fetchUsers = async () => {
        setUsersLoading(true);
        setUsersError(null);
        try {
          const { data } = await axios.get('/api/users', { headers: { Authorization: `Bearer ${userInfo.token}` }});
          setAllUsers(data.filter(u => u._id !== userInfo._id));
        } catch (e) {
          setUsersError('Failed to load users');
        } finally { setUsersLoading(false); }
      };
      fetchUsers();
    }
  }, [isAdmin, userInfo]);

  useEffect(() => { if (error && window.grecaptcha) window.grecaptcha.reset(); }, [error]);

  // adjust query when recipient changes (admin)
  useEffect(() => { if (isAdmin) setRecipientQuery(recipient || ''); }, [isAdmin, recipient]);

  useEffect(() => { return () => { if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current); }; }, []);

  const onChange = (value) => setGrecaptcha(value);
  const handleSendMessage = () => setShowConfirmation(true);
  const handleContentChange = (e) => {
    let val = stripControlChars(removeNewlines(e.target.value));
    if (val.length > MAX_MESSAGE_LENGTH) val = val.slice(0, MAX_MESSAGE_LENGTH);
    setContent(val); setCharCount(val.length); if (messageError) setMessageError('');
  };

  const handleRecipientFocus = () => { if (isAdmin && allUsers.length) setShowUserDropdown(true); };
  const handleRecipientInputChange = (e) => {
    const val = e.target.value; setRecipientQuery(val);
    if (recipient && val.toLowerCase() !== recipient.toLowerCase()) { setRecipient(''); setRecipientIsContact(false); }
    setHighlightedIndex(-1); if (!showUserDropdown) setShowUserDropdown(true);
    setValidationWarning(val && !recipient ? 'Select a valid recipient from the list.' : '');
  };
  const handleRecipientSelect = (user) => {
    setRecipient(user.username); setRecipientIsContact(!!user.isContact); setRecipientQuery(user.username); setShowUserDropdown(false); setHighlightedIndex(-1); setValidationWarning('');
    setTimeout(() => { if (messagesListRef.current) messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight; }, 50);
  };
  const handleRecipientKeyDown = (e) => {
    if (!showUserDropdown && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) { setShowUserDropdown(true); return; }
    if (!showUserDropdown) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightedIndex(p => (p + 1 >= filteredUsers.length ? 0 : p + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightedIndex(p => (p - 1 < 0 ? filteredUsers.length - 1 : p - 1)); }
    else if (e.key === 'Enter') { if (highlightedIndex >=0 && filteredUsers[highlightedIndex]) { e.preventDefault(); handleRecipientSelect(filteredUsers[highlightedIndex]); } }
    else if (e.key === 'Escape') { setShowUserDropdown(false); setHighlightedIndex(-1); }
    else if (e.key === 'Tab') { if (highlightedIndex >=0 && filteredUsers[highlightedIndex]) handleRecipientSelect(filteredUsers[highlightedIndex]); else setShowUserDropdown(false); }
  };
  const handleRecipientBlur = () => { blurTimeoutRef.current = setTimeout(() => { setShowUserDropdown(false); setHighlightedIndex(-1); if (recipientQuery && !recipient) setValidationWarning('Select a valid recipient from the list.'); }, 150); };

  useEffect(() => { // outside click
    function handleClickOutside(e){ if(!showUserDropdown) return; if (recipientInputRef.current?.contains(e.target)) return; if (dropdownRef.current?.contains(e.target)) return; setShowUserDropdown(false); setHighlightedIndex(-1);} 
    document.addEventListener('mousedown', handleClickOutside); return ()=> document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserDropdown]);

  const confirmSendMessage = async () => {
    if (isAdmin && !recipient) { setValidationWarning('Select a valid recipient from the list.'); return; }
    const sanitized = sanitizeOutgoingMessage(content);
    if (!sanitized) { setMessageError('Message cannot be empty.'); return; }
    if (containsProhibitedMarkup(sanitized)) { setMessageError('HTML or script tags are not allowed.'); return; }
    try {
      await dispatch(sendMessage({ sender: userInfo.username, recipient, content: sanitized, grecaptcha })).unwrap();
      setShowConfirmation(false); setContent(''); setCharCount(0); setMessageError(''); if (window.grecaptcha) window.grecaptcha.reset(); setGrecaptcha('');
      await dispatch(getMessages(userInfo.username));
    } catch (e) { console.error('Failed to send message:', e); }
  };
  const cancelSendMessage = () => setShowConfirmation(false);

  return (
    <Container>
      <Row>
        <Col>
          <h1>User</h1>
          <Form>
            {isAdmin ? (
              <Form.Group controlId="recipient" style={{ position: 'relative' }}>
                <Form.Label>Send To</Form.Label>
                <Form.Control type="text" ref={recipientInputRef} value={recipientQuery} placeholder="Search users by username or email..." onFocus={handleRecipientFocus} onChange={handleRecipientInputChange} onKeyDown={handleRecipientKeyDown} onBlur={handleRecipientBlur} aria-haspopup="listbox" aria-expanded={showUserDropdown} aria-activedescendant={highlightedIndex >=0 && filteredUsers[highlightedIndex] ? `user-opt-${filteredUsers[highlightedIndex]._id}`: undefined} />
                {showUserDropdown && (
                  <div ref={dropdownRef} className="user-suggest-dropdown" role="listbox" style={{ position:'absolute', top:'100%', left:0, width:'100%', maxHeight:'220px', overflowY:'auto', background:'#fff', border:'1px solid #ced4da', zIndex:1000, borderRadius:'.25rem', boxShadow:'0 2px 6px rgba(0,0,0,.12)'}}>
                    {filteredUsers.length ? filteredUsers.slice(0,50).map((u,i)=> (
                      <div key={u._id} id={`user-opt-${u._id}`} role="option" aria-selected={i===highlightedIndex} className={`dropdown-item${i===highlightedIndex?' active':''}`} style={{ padding:'.375rem .75rem', cursor:'pointer', fontSize:'.9rem', lineHeight:1.2, background: i===highlightedIndex ? '#0d6efd':'#fff', color: i===highlightedIndex ? '#fff':'#212529'}} onMouseDown={(e)=>{ e.preventDefault(); handleRecipientSelect(u); }}>
                        {u.username}{u.isContact ? ' (contact)' : (u.email?` (${u.email})`:'')}
                      </div>)) : <div className="dropdown-empty" style={{ padding:'.5rem .75rem', fontSize:'.85rem', color:'#666' }}>No matches</div>}
                  </div>) }
                {usersLoading && <div className="small text-muted mt-1">Loading users...</div>}
                {usersError && <div className="text-danger small mt-1">{usersError}</div>}
                {validationWarning && !recipient && <div className="text-danger small mt-1">{validationWarning}</div>}
              </Form.Group>
            ) : (<h3 className="mb-3">Messaging Support</h3>)}
            <Form.Group controlId="content">
              <Form.Label>Message</Form.Label>
              <Form.Control as="textarea" rows={3} value={content} onChange={handleContentChange} />
              <div className="text-end small text-muted">{charCount}/{MAX_MESSAGE_LENGTH}</div>
              {messageError && <div className="text-danger small mt-1">{messageError}</div>}
            </Form.Group>
            <ReCAPTCHA sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''} onChange={onChange} />
            <Button variant="primary" onClick={handleSendMessage} disabled={(isAdmin && !recipient) || !content.trim() || !!messageError}>Send Message</Button>
            {error && <div className="error mt-2 text-danger">{error}</div>}
          </Form>
        </Col>
        <Col>
          <h1>Messages</h1>
          {loading ? <Loader /> : (
            <div>
              {isAdmin && !recipient && <div className="p-3 text-muted border rounded" style={{ background:'#f8f9fa' }}>Select a recipient to view conversation.</div>}
              {isAdmin && recipient && !conversationMessages.length && <div className="p-3 text-muted border rounded" style={{ background:'#f8f9fa' }}>No messages yet with {recipient}.</div>}
              {isAdmin && recipient && conversationMessages.length > 0 && <div className="small text-muted mb-1">{conversationMessages.length} message(s)</div>}
              {!isAdmin && messages && messages.length === 0 && <div className="p-3 text-muted border rounded" style={{ background:'#f8f9fa' }}>No messages yet.</div>}
              <ListGroup ref={messagesListRef} style={{ maxHeight:'400px', overflowY:'auto' }}>
                {(isAdmin ? conversationMessages : messages).map(msg => (
                  <ListGroup.Item key={msg._id}>
                    <strong>From:</strong> {msg.sender}<br />
                    <strong>To:</strong> {msg.recipient}<br />
                    <strong>Message:</strong> {msg.content}<br />
                    <strong>Date:</strong> {formatTimestamp(msg.timestamp)}
                  </ListGroup.Item>))}
              </ListGroup>
            </div>) }
        </Col>
      </Row>
      <Modal show={showConfirmation} onHide={cancelSendMessage}>
        <Modal.Header closeButton><Modal.Title>Confirm</Modal.Title></Modal.Header>
        <Modal.Body>Are you sure you want to send this message?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cancelSendMessage}>Cancel</Button>
            <Button variant="primary" onClick={confirmSendMessage} disabled={(isAdmin && !recipient) || !content.trim() || !!messageError}>Send</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default SupportMessageScreen;

// import React, { useState, useEffect } from 'react';
// import { Container, Row, Col, Form, Button, ListGroup, Modal } from 'react-bootstrap';
// import { useDispatch, useSelector } from 'react-redux';
// import { sendMessage, getMessages } from '../actions/supportMessageActions';
// import ReCAPTCHA from "react-google-recaptcha";
// import Loader from '../components/Loader';

// const App = ({ location, history }) => {
//   const [username, setUsername] = useState('');
//   const [filterUsername, setFilterUsername] = useState('');
//   const [content, setContent] = useState('');
//   const [grecaptcha, setGrecaptcha] = useState('');
//   const [showConfirmation, setShowConfirmation] = useState(false);
//   const [recipient, setRecipient] = useState('Adminguy007');
//   const dispatch = useDispatch();
//   const userDetails = useSelector((state) => state.userDetails);
//   const { loading, user } = userDetails;
//   const userLogin = useSelector((state) => state.userLogin);
//   const { userInfo } = userLogin;
//   const { messages, error } = useSelector(state => state.supportMessage || {});

//   useEffect(() => {
//     if (!userInfo) {
//       history.push('/login');
//     } else {
//       setUsername(user.username);
//       // setEmail(user.email);
//     }
//   }, [dispatch, history, userInfo, user]);

//   useEffect(() => {
//     if (error) {
//       window.grecaptcha.reset();
//     }
//   }, [error]);

//   useEffect(() => {
//     const user = userInfo.username;
//     dispatch(getMessages(user));
//   }, [dispatch, userInfo]);

//   const onChange = (value) => {
//     setGrecaptcha(value);
//   };

//   const handleSendMessage = () => {
//     setShowConfirmation(true);
//   };

//   const confirmSendMessage = () => {
//     const sender = userInfo.username;
//     dispatch(sendMessage(sender, recipient, content, grecaptcha));
//     setShowConfirmation(false);
//     dispatch(getMessages(userInfo.username));
//     window.grecaptcha.reset();
//   };

//   const cancelSendMessage = () => {
//     setShowConfirmation(false);
//   };

//   const getUniqueUsernames = () => {
//     const usernames = new Set();
//     messages.forEach(message => {
//       usernames.add(message.sender);
//       usernames.add(message.recipient);
//     });
//     return Array.from(usernames);
//   };

//   return (
//     <Container>
//       <Row>
//         <Col>
//           <h1>User</h1>
//           <Form>
//           <Form.Group controlId="filterUsername">
//               <Form.Label>Filter by Username</Form.Label>
//               <Form.Control as="select" value={filterUsername} onChange={e => { setFilterUsername(e.target.value); setRecipient(e.target.value); }}>
//                 <option value="">All Users</option> 
//                 {getUniqueUsernames().map(username => (
//                   <option key={username} value={username}>{username}</option>
//                 ))}
//               </Form.Control>
//             </Form.Group>
//             <Form.Group controlId="content">
//               <Form.Label>Message</Form.Label>
//               <Form.Control as="textarea" rows={3} value={content} onChange={e => setContent(e.target.value)} />
//             </Form.Group>
//             <ReCAPTCHA
//               sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
//               onChange={onChange}
//             />
//             <Button variant="primary" onClick={handleSendMessage}>
//               Send Message
//             </Button>
//             {error && <div className="error">{error}</div>}
//           </Form>
//         </Col>
//         <Col>
//           <h1>Administrator</h1>
//             {loading ? <Loader /> :
//               <ListGroup style={{ maxHeight: '400px', overflowY: 'auto' }}>
//                 {messages &&
//                   messages.filter(message => {
//                     return (
//                       message.sender.includes(filterUsername) || message.recipient.includes(filterUsername)
//                     );
//                   }).map((message) => (
//                     <ListGroup.Item key={message._id}>
//                       <strong>From: </strong>
//                       {message.sender} ({message.sender})<br />
//                       <strong>To: </strong>
//                       {message.recipient} ({message.recipient})<br />
//                       <strong>Message: </strong>
//                       {message.content}<br />
//                       <strong>Date/Time: </strong>
//                       {message.timestamp}
//                     </ListGroup.Item>
//                   ))}
//               </ListGroup>}
//         </Col>
//       </Row>
//       {/* Custom Confirmation Modal */}
//       <Modal show={showConfirmation} onHide={cancelSendMessage}>
//         <Modal.Header closeButton>
//           <Modal.Title>Confirmation</Modal.Title>
//         </Modal.Header>
//         <Modal.Body>
//           Are you sure you want to send this message?
//         </Modal.Body>
//         <Modal.Footer>
//           <Button variant="secondary" onClick={cancelSendMessage}>
//             No
//           </Button>
//           <Button variant="primary" onClick={confirmSendMessage}>
//             Yes
//           </Button>
//         </Modal.Footer>
//       </Modal>
//     </Container>
//   );
// };

// export default App;

// import React, { useState, useEffect } from 'react';
// import { Container, Row, Col, Form, Button, ListGroup, Modal } from 'react-bootstrap';
// import { useDispatch, useSelector } from 'react-redux';
// import { sendMessage, getMessages } from '../actions/supportMessageActions';
// import ReCAPTCHA from "react-google-recaptcha"
// import Loader from '../components/Loader'



// const App = ({ location, history }) => {

//   const [name, setName] = useState('')
//   const [username, setUserame] = useState('')
  
//   const dispatch = useDispatch();
//   const userDetails = useSelector((state) => state.userDetails)
//   const { loading, user } = userDetails
//   const userLogin = useSelector((state) => state.userLogin)
//   const { userInfo } = userLogin
//   const [showConfirmation, setShowConfirmation] = useState(false);

//   const { messages, error } = useSelector(state => state.supportMessage || {});
//   const [recipient, setRecipient] = useState('Adminguy007');
//   const [content, setContent] = useState(''); 
//   const [semail, setEmail] = useState('');
//   const [grecaptcha, setGrecaptcha] = useState('')
//   function onChange(value) {
//     console.log('Captcha value:', value)
//     setGrecaptcha(value)
//   }
 
  
//   useEffect(() => {
//     if (!userInfo) {
//       history.push('/login')
//     } else {
//         setUserame(user.username)
//         setEmail(user.email)
//       }
//   }, [dispatch, history, userInfo, user])

//   useEffect(() => {
//     if (error) {
//       window.grecaptcha.reset()
//     }
//   }, [error])

  

//   const handleSendMessage = () => {
//     setShowConfirmation(true);
//   };

//   const confirmSendMessage = () => {
//     // Assuming 'user' is the current user. You can replace it with the actual user details.
//     const sender = userInfo.username;
//     dispatch(sendMessage(sender, recipient, content, grecaptcha));
//     setShowConfirmation(false);
//     dispatch(getMessages(userInfo.username))
//     window.grecaptcha.reset()
//     dispatch(getMessages(userInfo.username))
//   };

//   const cancelSendMessage = () => {
//     setShowConfirmation(false);
//     // You can add additional logic if needed when the user cancels
//   }
// useEffect(() => {
//     // Assuming 'user' is the current user. You can replace it with the actual user details.
//     const user = userInfo.username;
//     console.log(user+"here")
//     dispatch(getMessages(user));
//   }, [dispatch, user]);
//   return (
//     <Container>
//       <Row>
//         <Col>
//           <h1>User</h1>
//           <Form>
//             {/* <Form.Group controlId="recipient">
//               <Form.Label>Administrator Name</Form.Label>
//               <Form.Control type="text" value={recipient} onChange={e => setRecipient(e.target.value)} />
//             </Form.Group> */}
//             <Form.Group controlId="content">
//               <Form.Label>Message</Form.Label>
//               <Form.Control as="textarea" rows={3} value={content} onChange={e => setContent(e.target.value)} />
//             </Form.Group>
//             <ReCAPTCHA controlId='grecaptcha'
//    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
//     onChange={onChange}
//   />
//             <Button variant="primary" onClick={handleSendMessage}>
//               Send Message
//             </Button>
//             {error && <div className="error">{error}</div>}
//           </Form>
//         </Col>
//         <Col>
//           <h1>Administrator</h1>
// {loading ? <Loader /> : <ListGroup style={{ maxHeight: '400px', overflowY: 'auto' }}>

          
// {messages &&
//   messages.map((message) => (
//     <ListGroup.Item key={message._id}>
//       <strong>From: </strong>
//       {message.sender} ({message.sender})<br />
//       <strong>To: </strong>
//       {message.recipient} ({message.recipient})<br />
//       <strong>Message: </strong>
//       {message.content}<br />
//       <strong>Date/Time: </strong>
//       {message.timestamp}
//     </ListGroup.Item>
//         ))}
//       </ListGroup>}
          
//         </Col>
//       </Row>
//       {/* Custom Confirmation Modal */}
//       <Modal show={showConfirmation} onHide={cancelSendMessage}>
//         <Modal.Header closeButton>
//           <Modal.Title>Confirmation</Modal.Title>
//         </Modal.Header>
//         <Modal.Body>
//           Are you sure you want to send this message?
//         </Modal.Body>
//         <Modal.Footer>
//           <Button variant="secondary" onClick={cancelSendMessage}>
//             No
//           </Button>
//           <Button variant="primary" onClick={confirmSendMessage}>
//             Yes
//           </Button>
//         </Modal.Footer>
//       </Modal>
//     </Container>
//   );
// };

// export default App;
